import { createStyles, makeUiStyles, Theme } from '../styles/styles';
import { isMobileApp } from '../mobile-app/mobileAppHandlers';
import { fontText, fontDisplay, fontDisplayExtraBold, StylesIndexInjections } from 'common-react';
import { focusStyle } from '../styles/mixins';

interface FundDetailsDialogStyleProps {
  colorCircleGraph?: string;
  titleLength?: number;
  photoBase64?: string;
  showAdditionalTab?: boolean;
  fundTypeColor?: string;
  processLayout?: boolean;
  hasSidebarNavbar?: boolean;
  safeViewTopHeight?: number;
}

export default makeUiStyles((theme: Theme) => {
  const xxlMaxWidth = 2180;
  const getNavOffset = ({ processLayout, hasSidebarNavbar = !processLayout }: FundDetailsDialogStyleProps) => (
    hasSidebarNavbar ? theme.space.navWidth : 0
  );
  const getBackgroundWidth = (props: FundDetailsDialogStyleProps) => {
    const navOffset = getNavOffset(props);
    return `min(calc(100dvw - var(--scrollbar-width) - ${navOffset}px), ${theme.space.fundDetailsBackgroundMaxWidth}px)`;
  };
  const getBackgroundSideMargin = (props: FundDetailsDialogStyleProps) => {
    const navOffset = getNavOffset(props);
    return `calc((100dvw - var(--scrollbar-width) - 100% - ${navOffset}px) / -2)`;
  };

  return createStyles({
  root: {
  },
  fundDetailsDialog: {
    '& .MuiPaper-root': {
      maxWidth: '100%',
      maxHeight: '100%',
      background: 'transparent',
      boxShadow: 'none',
      width: '83%',
      margin: 0,
      marginLeft: 'auto',
      position: 'relative',

      '&.MuiAccordion-root': {
        marginLeft: 0,
        width: '100%',
      }
    },
    '& .MuiDialogContent-root': {
      padding: 0,
      marginLeft: 'auto'
    },
    '& .whiteBackground': {
      background: theme.color.greys[0],
    },
    '& .greyBackground': {
      background: theme.color.greys[10],
    },
  },
  closeButtonWrapper: {
    padding: 0,
    display: 'flex',
    flexDirection: 'row-reverse',
    marginBottom: 15,
    [theme.breakpoints.down('sm')]: {
      marginRight: 10,
    },
    '& > *': {
      cursor: 'pointer',
      lineHeight: '20px',
    },
    '& svg': {
      padding: 0,
      height: 20,
      width: 20,
    },
  },
  fundNameWrapper: {
    padding: '28px 40px 16px',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: '20px 16px 0',
    },
  },
  iconWrapper: {
    marginRight: 6,
    flexShrink: 0,
  },
  fundName: {
    font: fontText('normal', 28, 32),
  },

  leftColumn: {
    background: theme.color.greys[0],
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      height: 'auto',
      maxWidth: 'auto',
      borderRadius: 0,
    },
  },
  chartBox: {
    padding: '10px 40px 16px',
    [theme.breakpoints.down('sm')]: {
      padding: '10px 16px 14px',
    },
  },
  rightColumn: {
    height: '100%',
    background: theme.variables.isDarkTheme ? theme.color.greys[20] : theme.color.greys[10],
    padding: '24px 28px 20px',
    [theme.breakpoints.down('sm')]: {
      height: 'auto',
      borderRadius: 0,
      padding: `20px 16px ${isMobileApp() ? theme.space.mobileNavHeight() : 0}px`,
      display: 'flex',
      flexDirection: 'column',
      boxShadow: 'none',
      '& .link--primary': {
        alignSelf: 'center',
      },
    },

  },
  bulletsContainer: {
    margin: '0 40px',
    paddingBottom: 28,
    [theme.breakpoints.down('sm')]: {
      margin: '0 16px',
      paddingBottom: 40,
    },
  },
  bulletsListWrapper: {
    padding: 0,
    '& .MuiListItem-root': {
      alignItems: 'flex-start',
      font: fontText('normal', theme.fontSize.base, 24),
      color: theme.color.greys[80],
      paddingTop: 6,
      paddingBottom: 6,
      paddingLeft: 0,
      paddingRight: 0,
      [theme.breakpoints.down('sm')]: {
        paddingRight: 0,
      },
      '&:before': {
        content: '"\\2022"',
        color: theme.color.oranges[50],
        display: 'inline-block',
        font: fontText('normal', theme.fontSize.base, 24),
        paddingRight: 8,
      },
      '&:first-child': {
        paddingTop: 0,
      }
    },
  },

  documentsListWrapper: {
    '& li': {
      padding: '16px 0',
      borderBottom: `1px solid ${theme.color.greys[15]}`,
      justifyContent: 'flex-start',
      '&:last-child': {
        borderBottom: 'none',
      }
    },
  },
  costGrid: {
    rowGap:16,
  },
  costItem: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  costItemInfo: {
    textAlign: 'left',
    marginTop: 8,
  },
  costInfo: {
    color: theme.color.greys[60],
    padding: '16px 0',
  },
  costsCalculatorLink: {
    textTransform: 'uppercase',
    marginBottom: 34,
    font: fontText('normal', 14, 16),
  },
  selectButton: {
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      marginBottom: 100,
    },
  },
  goToFundDetailsButton: {
    width: '100%',
    textAlign: 'center',
    textTransform: 'uppercase',
    font: fontText('normal', 14, 20),
    padding: '20px 12px',
  },
  container: {
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      margin: `0 -${theme.space.appPadding}px 40px`,
      width: 'auto',
      paddingBottom: 28
    },
    '& > .MuiGrid-container': {
      height: '100vh'
    }
  },
  bulletsAccordion: {
    [theme.breakpoints.down('sm')]: {
      marginBottom: 20
    },

    '& .MuiAccordionDetails-root': {
      flexDirection: 'column',
    },

    '&.MuiAccordion-root .MuiAccordionSummary-content': {
      margin: '12px 0'
    },
    '&.MuiPaper-root': {
      width: '100%'
    }
  },
  bulletsAccordionTitle: {
    textTransform: 'initial',
  },
  closeIcon: {
    cursor: 'pointer',
    display: 'block',
    marginLeft: 'auto',
    '& path': {
      fill: theme.color.greys[80],
    }
  },
  customButton: {
    [theme.breakpoints.down('sm')]: {
      backgroundColor: 'transparent',
      border: 'none',
      width: '100%',
      margin: 0,
      padding: 0,
      textAlign: 'left',

      '&:hover, &:focus': {
        border: 'none',
        outline: 'none',
      },

      '&:focus-visible': {
        ...focusStyle(theme),
      },
    },
  },
  item: {
    padding: 0,
    borderBottom: `1px solid ${theme.color.greys[10]}`,
    '& + $item': {
      marginTop: 12,
      [theme.breakpoints.up('xs')]: {
        marginTop: 16,
      },
    },
  },
  accordionTitle: {
    padding: '16px 0px 28px',
    font: fontDisplay('normal', theme.fontSize.base, 20),
    color: theme.color.greys[80],
    textAlign: 'left',
    [theme.breakpoints.down('xs')]: {
      padding: '16px 0',
      font: fontText('normal', theme.fontSize.base, 20),
    }
  },
  accordionDetails: {
    textAlign: 'left',
    padding: '8px 0 20px',
    background: theme.color.greys[0],
    font: fontText('normal', theme.fontSize.s, 18),
    color: theme.color.greys[60],
    display: 'block',
    [theme.breakpoints.down('xs')]: {
      padding: '16px 0px',
    },
    '& > li': {
      marginLeft: 15,
      marginTop: 10,
    }
  },
  layout: {
    background: `${theme.color.greys[20]} !important`,
    maxWidth: 'initial',
    paddingBottom: 60,
    //Add extra top margin to compensate for the header height
    marginTop: 1,
    [theme.breakpoints.down('sm')]: {
      paddingBottom: 20,
      paddingTop: 0,
      marginTop: ({ safeViewTopHeight }: FundDetailsDialogStyleProps) => safeViewTopHeight,
    },
  },
  backgroundImageWrapper: {
    position: 'relative',
    top: -1,
    height: 630,
    maxWidth: theme.space.fundDetailsBackgroundMaxWidth,
    [theme.breakpoints.up('lg')]: {
      width: getBackgroundWidth,
      marginLeft: getBackgroundSideMargin,
      marginRight: getBackgroundSideMargin,
    },
    [theme.breakpoints.up(xxlMaxWidth)]: {
      width: getBackgroundWidth,
      marginLeft: () => 'auto',
      marginRight: () => 'auto',
      left: ({ processLayout, hasSidebarNavbar = !processLayout }: FundDetailsDialogStyleProps) => (
        hasSidebarNavbar && '-25%'),
    },
    [theme.breakpoints.down('md')]: {
      marginLeft: -16,
      marginRight: -16,
    },
    [theme.breakpoints.down('sm')]: {
      height: 467
    },
    [theme.breakpoints.down('xs')]: {
      height: ({ titleLength }) => (() => {
        if (titleLength && titleLength > 100) {
          return 492;
        }
        return 467;
      })()
    },
  },
  backgroundImage: {
    background: theme.color.constants.greys[70],
    height: '100%',
    width: '100%',
    backgroundSize: 'cover',
    backgroundPosition: 'right',
    [theme.breakpoints.down('sm')]: {
      backgroundPosition: '50% 50%',
    },
  },
  backgroundImageOverlay: {
    position: 'relative',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    padding: '124px 60px 48px',
    maxWidth: () => theme.space.bottomBarMaxWidth,
    margin: '0 auto',
    [theme.breakpoints.down('sm')]: {
      padding: '98px 16px 32px',
    },

    '& $fundListItemIcons': {
      marginBottom: 0,
      marginTop: 24
    },
  },
  fundNameTitle: {
    marginBottom: 6,
    marginTop: 6,
    maxWidth: 700,
    color: theme.color.greys[80],
    [theme.breakpoints.down('sm')]: {
      marginTop: 8,
      font: fontDisplayExtraBold('normal', theme.fontSize.xl, 30),
    },
  },
  headerContent: {
    backgroundColor: theme.color.greys[0],
    padding: 24,
    borderRadius: 16,
    width: 700,
    [theme.breakpoints.down('sm')]: {
      padding: '24px 16px',
    },
  },
  previousName: {
    color: theme.color.greys[60],
    textTransform: 'capitalize'
  },
  downloadFundCardLink: {
    marginTop: 8,
  },
  actionBar: {
    position: 'relative',
    overflow: 'visible',
    paddingBottom: 24,
    maxWidth: theme.space.fundDetailsMaxWidthDefault,
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      marginBottom: 16,
      paddingBottom: 0,
    },
    [theme.breakpoints.down('xs')]: {
      margin: '0px auto 16px auto',
    },

  },
  actionTopBarContextBox: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      marginTop: 24
    },
    '& $actionTopBarButton': {
      [theme.breakpoints.down('sm')]: {
        width: '100%'
      },
    }
  },
  actionBarInfoBox: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: 'auto auto',
    columnGap: 24,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      columnGap: 16,
    },
  },
  actionBarInfoCol: {
    display: 'grid',
    gridRow: 'span 2',
    gridTemplateRows: 'subgrid',
    rowGap: 16,
    alignContent: 'start',
  },
  actionBarInfoCell: {
    borderLeft: `1px solid ${theme.color.greys[15]}`,
    paddingLeft: 24,
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
      borderLeft: 'none',
    },
  },
  tooltip: {},
  typeFundLabel: {
    paddingBottom: 4,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  typeFund: {
    position: 'relative',
    display: 'inline-block',
    font: fontText('normal', theme.fontSize.base, 24),
    textTransform: 'capitalize',
    color: theme.color.greys[80],
    paddingLeft: 10,
    [theme.breakpoints.down('sm')]: {
      marginRight: 4,
    },

    '&:before': {
      position: 'absolute',
      transform: 'translateY(-50%)',
      top: 'calc(50%)',
      left: 0,
      zIndex: 1,
      content: '""',
      display: 'block',
      width: 4,
      height: '100%',
      backgroundColor: ({ fundTypeColor }) => `#${fundTypeColor}`,
    }
  },
  ratingAolLabel: {
    paddingBottom: 4,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    [theme.breakpoints.down('sm')]: {
      paddingBottom: 4,
    },
    [theme.breakpoints.down('xs')]: {
      maxWidth: 100
    },
    '& $alertCircleIcon': {
      top: 1,
    }
  },
  rating: {
    display: 'flex',
  },
  emptyStarIcon: {
    color: theme.color.greys[20],
  },
  starIcon: {
    color: theme.color.oranges[50],
  },
  horizonLabel: {
    paddingBottom: 4,
    display: 'flex',
    gap: 4,
    alignItems: 'center',
    '& $alertCircleIcon': {
      top: 1
    },
    [theme.breakpoints.down('xs')]: {
      flexWrap: 'wrap',
    },
  },
  horizon: {
    font: fontText('normal', theme.fontSize.s, 20),
    backgroundColor: theme.color.greys[80],
    color: theme.color.greys[0],
    padding: '2px 8px',
    display: 'inline-block',
    borderRadius: 4,
  },
  modal: {
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: 0,
    },
  },
  fundBigChartCard: {
    maxWidth: theme.space.fundDetailsMaxWidthDefault,
    margin: '0 auto 24px auto',
    [theme.breakpoints.down('sm')]: {
      marginBottom: 16,
    },
  },
  grid: {
    display: 'flex',
    maxWidth: theme.space.fundDetailsMaxWidthDefault,
    margin: '0 auto 40px auto',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column'
    },

    '& > div > div:not(:last-child)': {
      marginBottom: 24
    },

    '& > div:first-child': {
      flexBasis: `calc(67% - ${24/2}px)`,
      margin: `0 ${24}px ${24}px 0`,
      flexGrow: 0,
      flexShrink: 0,
      [theme.breakpoints.down('sm')]: {
        margin: '0 0 40px',
        flexBasis: '100%'
      },
    },

    '& > div:last-child': {
      flexBasis: `calc(33% - ${24/2}px)`,
      margin: `0 ${24}px ${24}px 0`,
      flexGrow: 0,
      flexShrink: 0,
      [theme.breakpoints.down('sm')]: {
        margin: 0,
        flexBasis: '100%'
      },
    }
  },
  tabPanel: {
    marginTop: 16,
    font: fontText('normal', theme.fontSize.base, 24),
  },
  infoGrid: {
    marginTop: 16,
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column'
    },

    '& > div': {
      flex: 1,
      marginRight: 24,
      [theme.breakpoints.down('sm')]: {
        marginRight: 0,
      },

      '& > div': {
        marginBottom: 16
      }
    }
  },
  infoLabel: {
    marginBottom: 8,
  },
  infoValue: {
    font: fontText('normal', theme.fontSize.base, 24),
  },
  fundManagerGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 24,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column'
    },
    '& > div:not(:last-child)': {
      [theme.breakpoints.down('sm')]: {
        marginBottom: 16
      },
    }
  },
  fundManagerBox: {
    position: 'relative',
    flex: 1,
    flexBasis: 'calc(50% - 24px)',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      flexBasis: '100%'
    },
    '& img': {
      width: 60,
      height: 60,
      position: 'relative',
      borderRadius: '100%',
    }
  },
  fundManagerImageBox: {
    position: 'relative',
    '&:after': {
      content: '""',
      position: 'absolute',
      top: -1,
      left: -1,
      zIndex: 1,
      display: 'block',
      width: 62,
      height: 62,
      backgroundColor: 'transparent',
      border: `2px solid ${theme.color.greys[0]}`,
      borderRadius: '100%'
    },
  },
  fundManagerNameBox: {
    paddingLeft: 16,

    '& > span:first-child': {
      font: fontDisplay('normal', theme.fontSize.base, 20),
      marginBottom: 2
    }
  },
  fundManagerTitle: {
    font: fontDisplay('normal', theme.fontSize.base, 20),
    marginBottom: 16,
    paddingTop: 8
  },
  bottomBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '0 auto',
    maxWidth: ({ processLayout }) => processLayout ? theme.space.maxWidth : theme.space.bottomBarMaxWidth,
    padding: '0 67px 0 67px',
    [theme.breakpoints.down(theme.breakpoints.values.lg)]: {
      maxWidth: ({ processLayout }) => processLayout ? theme.space.maxWidth : theme.space.bottomBarMaxWidth,
      padding: '0 56px 0 42px',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '0 0 16px',
      alignItems: 'center'
    },
  },
  bottomBarFundName: {
    color: theme.color.greys[80],
    [theme.breakpoints.down('sm')]: {
      paddingRight: 4,
    },
  },
  walletSectionMultiButtonWrapper: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },

    '& > span:first-child': {
      font: fontText('normal', theme.fontSize.base, 24),
      color: theme.color.greys[80],
      paddingRight: 8,
      [theme.breakpoints.down('sm')]: {
      font: fontText('normal', theme.fontSize.s, 20),

        paddingRight: 0,
        paddingBottom: 4,
      },
    }
  },
  walletValuesBox: {
    display: 'flex',
    alignItems: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      columnGap: 16,
    },

    '& div:not(:last-child)': {
      marginRight: 32,
      [theme.breakpoints.down('sm')]: {
        marginRight: 0,
        marginBottom: 16
      },
    }
  },
  walletValue: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  walletCharBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      alignItems: 'stretch',
    },
  },
  accordionCardTitle: {
    font: fontText('normal', theme.fontSize.l, 28),
    color: theme.color.greys[80]
  },
  additionalTab: {
    maxWidth: theme.space.fundDetailsMaxWidthDefault,
    margin: '0 -24px',
    padding: '16px 24px',
    backgroundColor: theme.color.oranges[50],
    color: theme.color.constants.greys[70],
    font: fontText('normal', theme.fontSize.s, 18),
    [theme.breakpoints.down('sm')]: {
      margin: '0 -16px',
      padding: '16px',
    },
  },
  fundCardDocument: {
    flexShrink: 0,
    color: theme.color.greys[80],
    marginTop: 8
  },
  aboutTabs: {
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
    },
  },
  processLayout: {
    maxWidth: 'initial',
    paddingBottom: 60,
    backgroundColor: theme.color.greys[20],
    [theme.breakpoints.down('sm')]: {
      paddingTop: () => isMobileApp() ? 16 : 28,
      marginTop: ({ safeViewTopHeight }: FundDetailsDialogStyleProps) => safeViewTopHeight,
    },
    [theme.breakpoints.down('xs')]: {
      padding: '28px 16px'
    },
  },
  chartBoxWrapper: {
    display: 'flex',
    justifyContent: 'center',
  },
  chartLegendWrapper: {
    width: '100%',
    marginTop: 16,
  },
  chartLegendTitle: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '8px 0',
    borderBottom: `1px solid ${theme.color.greys[10]}`,
    color: theme.color.greys[80],
    fontSize: '14px',
  },
  chartLegendItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: `1px solid ${theme.color.greys[10]}`,
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  chartLegendItemColor: {
    width: '4px',
    height: '24px',
    marginRight: '12px',
    borderRadius: '2px'
  },
  chartLegendItemActive: {
    boxShadow: theme.boxShadow.small,
  },
  chartLegendItemName: {
    flex: 1,
    font: fontText('normal', theme.fontSize.base, 24),
  },
  chartLegendItemValue: {
    font: fontText('normal', theme.fontSize.base, 24),
  },
  actionTopBarButton: {},
  fundListItemIcons: {},
  expandableTextButton: {}
  });
}, { name: 'FundDetailsDialog', index: StylesIndexInjections.UI_MOLECULES });
